// PrivacyPolicy.jsx
import React, { useMemo, useRef } from "react";
import "./PrivacyPolicy.css";
import { motion } from "framer-motion";
import { useLocalContext } from "../../context/LocalContext";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65 } },
};

const PrivacyPolicy = () => {
  const { webinfo = {} } = useLocalContext();

  const company = webinfo?.name || "Our Company";
  const email = webinfo?.email || "hello@yourdomain.com";
  const phoneText = webinfo?.phone || webinfo?.phonecall || "";
  const phoneCall = webinfo?.phonecall || webinfo?.phone || "";

  // section refs for TOC scroll
  const refs = useRef({});

  const sections = useMemo(
    () => [
      {
        id: "intro",
        title: "Introduction",
        content: (
          <>
            <p className="pp__p">
              At <b>{company}</b>, we respect your privacy and explain how we handle information when you browse our
              digital marketing website, use our contact form, or email us about services.
            </p>
            <div className="pp__notice">
              <b>Quick summary:</b> We collect limited data needed to respond to you, run basic analytics, and keep the
              site secure. We do not sell personal information as a standalone product.
            </div>
          </>
        ),
      },
      {
        id: "collect",
        title: "Information we collect",
        content: (
          <>
            <p className="pp__p">Depending on how you use the site, we may collect:</p>
            <ul className="pp__list">
              <li>Contact details: name, email, phone, company (if provided).</li>
              <li>Inquiry details: messages, requirements, attachments you submit.</li>
              <li>Technical data: device type, browser, IP address, basic usage signals.</li>
              <li>Cookies/analytics data to understand traffic and improve performance.</li>
            </ul>
          </>
        ),
      },
      {
        id: "use",
        title: "How we use information",
        content: (
          <>
            <ul className="pp__list">
              <li>To respond to your inquiry and provide support.</li>
              <li>To deliver services, updates, and project communication.</li>
              <li>To improve website content, speed, and user experience.</li>
              <li>To prevent abuse, spam, or security issues.</li>
              <li>To comply with legal obligations where applicable.</li>
            </ul>
          </>
        ),
      },
      {
        id: "cookies",
        title: "Cookies & analytics",
        content: (
          <>
            <p className="pp__p">
              We may use cookies and analytics tools to understand what pages are
              used most, which content performs best, and where users face issues.
              You can disable cookies in your browser settings, but some features
              may not work as expected.
            </p>
          </>
        ),
      },
      {
        id: "security",
        title: "Security & data protection",
        content: (
          <>
            <p className="pp__p">
              We use reasonable, industry-standard safeguards such as HTTPS,
              access controls, and secure tooling. However, no online system is
              100% secure, so we encourage you to avoid sharing sensitive
              information via forms.
            </p>
            <div className="pp__notice">
              <b>Tip:</b> If you need to share credentials, use a secure password
              manager / vault link and time-limited access.
            </div>
          </>
        ),
      },
      {
        id: "thirdparty",
        title: "Third-party services",
        content: (
          <>
            <p className="pp__p">
              We may use trusted third-party tools (e.g., analytics, CRM, email,
              hosting) to operate the website and deliver services. These
              providers are expected to protect your data and process it only for
              intended purposes.
            </p>
          </>
        ),
      },
      {
        id: "rights",
        title: "Your rights",
        content: (
          <>
            <ul className="pp__list">
              <li>Request access to the personal data we hold about you.</li>
              <li>Request correction of inaccurate information.</li>
              <li>Request deletion where applicable.</li>
              <li>Withdraw consent for marketing communications.</li>
            </ul>
          </>
        ),
      },
      {
        id: "usprivacy",
        title: "State privacy rights",
        content: (
          <>
            <p className="pp__p">
              Depending on where you live, state or regional laws (for example California, Virginia, Colorado,
              Connecticut, and others) may give you additional rights over personal information. These can include:
            </p>
            <ul className="pp__list">
              <li>The right to know what categories of personal information we collect and how we use them.</li>
              <li>The right to request access to or deletion of certain personal information, subject to exceptions.</li>
              <li>The right to opt out of sale or sharing of personal information for cross-context behavioral
                advertising, where those terms apply under your state law.</li>
              <li>The right not to receive discriminatory treatment for exercising privacy rights.</li>
            </ul>
            <p className="pp__p">
              To exercise rights, email us at the address in the Contact section. We may need to verify your identity
              before responding. You may use an authorized agent where applicable law allows. If we deny a request, you
              may have a right to appeal where required by law.
            </p>
          </>
        ),
      },
      {
        id: "children",
        title: "Children’s privacy",
        content: (
          <>
            <p className="pp__p">
              Our services are not intended for individuals under 16, and we do
              not knowingly collect data from children.
            </p>
          </>
        ),
      },
      {
        id: "updates",
        title: "Policy updates",
        content: (
          <>
            <p className="pp__p">
              We may update this policy from time to time. The latest version
              will always be available on this page.
            </p>
          </>
        ),
      },
      {
        id: "contact",
        title: "Contact",
        content: (
          <div className="pp__contact">
            <p className="pp__p">
              Questions about this Privacy Policy or your data? Contact us:
            </p>
            <p className="pp__p">
              Email: <a href={`mailto:${email}`}>{email}</a>
              <br />
              {phoneText ? (
                <>
                  Phone:{" "}
                  <a href={phoneCall ? `tel:${phoneCall}` : "#"}>{phoneText}</a>
                </>
              ) : null}
            </p>

            <div className="pp__actions">
              <a className="pp__btn pp__btn--primary" href={`mailto:${email}`}>
                Email us →
              </a>
              <a className="pp__btn pp__btn--ghost" href="/contact">
                Contact page
              </a>
            </div>
          </div>
        ),
      },
    ],
    [company, email, phoneText, phoneCall]
  );

  const scrollTo = (id) => {
    const el = refs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="pp" aria-label="Privacy Policy">
      <div className="pp__wrap">
        {/* HERO */}
        <motion.div
          className="pp__hero"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.4 }}
          variants={fadeUp}
        >
          <div>
            <div className="pp__badge">
              <span className="dot" aria-hidden="true" />
              Privacy
            </div>

            <h1 className="pp__title">
              Privacy Policy for <span>{company}</span>
            </h1>

            <p className="pp__sub">
              Your trust matters. This policy explains what we collect, why we
              collect it, and how you can request changes or deletion.
            </p>
          </div>

          <div className="pp__meta">
            <p>
              <b>Effective:</b> Immediate
              <br />
              <b>Applies to:</b> Website, forms, emails & service inquiries
            </p>
          </div>
        </motion.div>

        {/* BODY */}
        <div className="pp__grid">
          {/* TOC */}
          <aside className="pp__toc" aria-label="Table of contents">
            <div className="pp__tocTitle">On this page</div>
            <ul className="pp__tocList">
              {sections.map((s) => (
                <li key={s.id}>
                  <button className="pp__tocBtn" onClick={() => scrollTo(s.id)}>
                    {s.title}
                  </button>
                </li>
              ))}
            </ul>
            <p className="pp__tocHint">
              Tip: click a section to jump. On mobile, the table appears above the content.
            </p>
          </aside>

          {/* CONTENT */}
          <motion.div
            className="pp__card"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
          >
            {sections.map((s) => (
              <section
                key={s.id}
                className="pp__section"
                ref={(el) => (refs.current[s.id] = el)}
              >
                <h3 className="pp__h3">{s.title}</h3>
                {s.content}
              </section>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;